import { api } from '@/store/api'
import type {
  Instruction,
  InstructionCreate,
  InstructionUpdate,
  InstructionListResponse,
  InstructionCounts,
  GetInstructionsParams,
  AccessType,
} from './types'
import { ALL_LIST_TAGS, arrayOrPaged } from './instructionsApi.tags'

export const instructionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getInstructions: build.query<InstructionListResponse, GetInstructionsParams>({
      query: ({ page, pageSize, q, accessType }) => ({
        url: 'instructions/',
        params: {
          page,
          page_size: pageSize,
          ...(q ? { q } : {}),
          ...(accessType ? { access_type: accessType } : {}),
        },
      }),
      transformResponse: arrayOrPaged,
      providesTags: (r) =>
        r
          ? [
              ...r.items.map(({ id }) => ({ type: 'Instruction' as const, id })),
              { type: 'Instruction' as const, id: 'LIST' },
            ]
          : [{ type: 'Instruction' as const, id: 'LIST' }],
    }),
    // Backend returns single article BY SLUG (per backend doc §2.2).
    // PATCH/DELETE still use {id}.
    getInstruction: build.query<Instruction, string>({
      query: (slug) => `instructions/${slug}`,
      providesTags: (r, _e, slug) =>
        r
          ? [
              { type: 'Instruction', id: r.id },
              { type: 'Instruction', id: slug },
            ]
          : [{ type: 'Instruction', id: slug }],
    }),
    getMyInstructions: build.query<
      InstructionListResponse,
      { accessType?: AccessType; page?: number; pageSize?: number; q?: string }
    >({
      query: (params) => ({
        url: 'instructions/my',
        params: {
          page: params.page,
          page_size: params.pageSize,
          ...(params.q ? { q: params.q } : {}),
          ...(params.accessType ? { access_type: params.accessType } : {}),
        },
      }),
      transformResponse: arrayOrPaged,
      providesTags: [{ type: 'Instruction' as const, id: 'MY_LIST' }],
    }),
    getInstructionCounts: build.query<InstructionCounts, void>({
      query: () => 'instructions/my/counts',
      providesTags: [{ type: 'Instruction' as const, id: 'COUNTS' }],
    }),
    getBookmarks: build.query<
      InstructionListResponse,
      { page: number; pageSize: number; scope?: 'studio' }
    >({
      query: ({ page, pageSize, scope }) => ({
        url: 'instructions/bookmarks',
        params: {
          page,
          page_size: pageSize,
          ...(scope ? { scope } : {}),
        },
      }),
      transformResponse: arrayOrPaged,
      providesTags: [{ type: 'Instruction', id: 'BOOKMARKS_LIST' }],
    }),
    addBookmark: build.mutation<void, string>({
      query: (slug) => ({ url: `instructions/${slug}/bookmark`, method: 'POST' }),
      invalidatesTags: ALL_LIST_TAGS,
    }),
    removeBookmark: build.mutation<void, string>({
      query: (slug) => ({ url: `instructions/${slug}/bookmark`, method: 'DELETE' }),
      invalidatesTags: ALL_LIST_TAGS,
    }),
    createInstruction: build.mutation<Instruction, InstructionCreate>({
      query: (body) => ({ url: 'instructions/', method: 'POST', body }),
      invalidatesTags: ALL_LIST_TAGS,
    }),
    updateInstruction: build.mutation<Instruction, { id: number; patch: InstructionUpdate }>({
      query: ({ id, patch }) => ({ url: `instructions/${id}`, method: 'PATCH', body: patch }),
      // Правка живой статьи меняет черновик, а не живую версию: без перечитывания
      // модерации форма засевалась бы прежним черновиком.
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Instruction', id },
        { type: 'ArticleModeration', id },
        ...ALL_LIST_TAGS,
      ],
    }),
    deleteInstruction: build.mutation<void, number>({
      query: (id) => ({ url: `instructions/${id}`, method: 'DELETE' }),
      // Don't invalidate the per-id tag — that triggers a refetch of getInstruction
      // for the still-mounted editor → 404. List tags suffice; the single-item cache
      // becomes orphaned but has no subscribers after navigation.
      invalidatesTags: ALL_LIST_TAGS,
    }),
  }),
})

export const {
  useGetInstructionsQuery,
  useGetInstructionQuery,
  useGetMyInstructionsQuery,
  useGetInstructionCountsQuery,
  useGetBookmarksQuery,
  useAddBookmarkMutation,
  useRemoveBookmarkMutation,
  useCreateInstructionMutation,
  useUpdateInstructionMutation,
  useDeleteInstructionMutation,
} = instructionsApi
